import { isNullOrUndefined, utc } from 'rewire-common';
import { getMultiple, IUdf } from '../DataTypes';

const currencyFormatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function formatCurrency(v: number) {
  if (isNullOrUndefined(v)) v = 0;
  return `${currencyFormatter.format(v).replace(/^(\D+)/, '$1 ')}`;
}

const emptyString = '';
const comma = ',';
const period = '.';
const minus = '-';
const minusRegExp = /-/;
const nonDigitsRegExp = /\D+/g;
const digitRegExp = /\d/;
const caretTrap = '[]';
const multiple = getMultiple();

export function createNumberMask({
  prefix = emptyString,
  suffix = emptyString,
  includeThousandsSeparator = false,
  thousandsSeparatorSymbol = comma,
  allowDecimal = false,
  decimalSymbol = period,
  decimalLimit = 2,
  requireDecimal = false,
  allowNegative = false,
  allowLeadingZeroes = false,
  allowMultiple = false,
  integerLimit = null,
} = {}) {
  const prefixLength = prefix?.length || 0;
  const suffixLength = suffix?.length || 0;
  const thousandsSeparatorSymbolLength = thousandsSeparatorSymbol?.length || 0;

  function numberMask(rawValue: string = emptyString) {
    if (allowMultiple && rawValue.localeCompare(multiple) === 0) {
      return rawValue.split(emptyString).map((_char) => /./);
    }

    const rawValueLength = rawValue.length;

    if (rawValue === emptyString || (rawValue[0] === prefix[0] && rawValueLength === 1)) {
      return prefix.split(emptyString).concat([digitRegExp.toString()]).concat(suffix.split(emptyString));
    } else if (rawValue === decimalSymbol && allowDecimal) {
      return prefix.split(emptyString).concat(['0', decimalSymbol, digitRegExp.toString()]).concat(suffix.split(emptyString));
    }

    const isNegative = rawValue[0] === minus && allowNegative;
    // If negative remove "-" sign
    if (isNegative) {
      rawValue = rawValue.toString().substr(1);
    }

    const indexOfLastDecimal = rawValue.lastIndexOf(decimalSymbol);
    const hasDecimal = indexOfLastDecimal !== -1;

    let integer: string | undefined;
    let fraction: (string | RegExp)[] | undefined;
    let mask: (string | RegExp)[];

    // remove the suffix
    if (rawValue.slice(suffixLength * -1) === suffix) {
      rawValue = rawValue.slice(0, suffixLength * -1);
    }

    if (hasDecimal && (allowDecimal || requireDecimal)) {
      integer = rawValue.slice(rawValue.slice(0, prefixLength) === prefix ? prefixLength : 0, indexOfLastDecimal);
      const f = rawValue.slice(indexOfLastDecimal + 1, rawValueLength);
      fraction = convertToMask(f.replace(nonDigitsRegExp, emptyString));
    } else {
      if (rawValue.slice(0, prefixLength) === prefix) {
        integer = rawValue.slice(prefixLength);
      } else {
        integer = rawValue;
      }
    }

    if (!isNullOrUndefined(integerLimit) && typeof integerLimit === 'number') {
      const thousandsSeparatorRegex = thousandsSeparatorSymbol === '.' ? '[.]' : `${thousandsSeparatorSymbol}`;
      const numberOfThousandSeparators = (integer.match(new RegExp(thousandsSeparatorRegex, 'g')) || []).length;

      integer = integer.slice(0, integerLimit! + numberOfThousandSeparators * thousandsSeparatorSymbolLength);
    }

    integer = integer.replace(nonDigitsRegExp, emptyString);

    if (!allowLeadingZeroes) {
      integer = integer.replace(/^0+(0$|[^0])/, '$1');
    }

    integer = includeThousandsSeparator ? addThousandsSeparator(integer, thousandsSeparatorSymbol) : integer;
    mask = convertToMask(integer);

    if ((hasDecimal && allowDecimal) || requireDecimal === true) {
      if (rawValue[indexOfLastDecimal - 1] !== decimalSymbol) {
        mask.push(caretTrap);
      }

      mask.push(decimalSymbol, caretTrap);

      if (fraction) {
        if (typeof decimalLimit === 'number') {
          fraction = fraction.slice(0, decimalLimit);
        }

        mask = mask.concat(fraction);
      }

      if (requireDecimal === true && rawValue[indexOfLastDecimal - 1] === decimalSymbol) {
        mask.push(digitRegExp);
      }
    }

    if (prefixLength > 0) {
      mask = prefix.split(emptyString).concat(mask?.map((m) => m.toString()));
    }

    if (isNegative) {
      // If user is entering a negative number, add a mask placeholder spot to attract the caret to it.
      if (mask.length === prefixLength) {
        mask.push(digitRegExp);
      }

      mask = [minusRegExp.toString()].concat(mask?.map((m) => m.toString()));
    }

    if (suffix.length > 0) {
      mask = mask.concat(suffix.split(emptyString));
    }

    return mask;
  }

  numberMask.instanceOf = 'createNumberMask';

  return numberMask;
}

function convertToMask(strNumber: string) {
  return strNumber.split(emptyString).map((char) => (digitRegExp.test(char) ? digitRegExp : char));
}

function addThousandsSeparator(n: string, thousandsSeparatorSymbol: string) {
  return n.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparatorSymbol);
}

export function createAccountCodeMask({
  // mask          = /[.a-zA-Z0-9_-]/,
  mask = /./,
  allowMultiple = false,
} = {}) {
  function accountCodeMask(rawValue: string = emptyString) {
    if (allowMultiple && rawValue.localeCompare(multiple) === 0) {
      return rawValue.split(emptyString).map((_char) => /./);
    }

    if (allowMultiple && rawValue.match(/[\u{00AB}\u{00BB}]/u)) {
      rawValue = rawValue.replace(/.+/g, emptyString);
    }

    return rawValue.split(emptyString).map((_char) => mask);
  }

  accountCodeMask.instanceOf = 'createAccountCodeMask';

  return accountCodeMask;
}

export function formatUdfValue(udf: IUdf, value?: any) {
  if (!value) {
    return;
  }
  switch (udf.type) {
    case 'boolean':
      value = isNullOrUndefined(value) ? undefined : value ? 'Yes' : 'No';
      break;
    case 'date':
      value = value.toDateString?.() ?? utc(value).toDateString();
      break;
    case 'reference':
      value = Array.isArray(value) ? value.map((v) => v.name).join(', ') : value.name;
      break;
    case 'expression':
    case 'float':
    case 'integer':
    case 'text':
    default:
      value = Array.isArray(value) ? value.join(', ') : value;
      break;
  }
  return value;
}

export const defaultPhoneFormat = '+# (###) ###-####';
export const defaultPhoneMask = 'x';

export function getPhoneString(value: any): string {
  if (!value) return value;
  if (isNaN(parseInt(value))) return value;

  const phoneStr = value.toString();
  const phoneFormat = defaultPhoneFormat;
  const phoneMask = defaultPhoneMask;
  let hashCount = 0;
  const formattedNumberArr = phoneFormat.split('');
  for (let i = 0; i < phoneFormat.length; i++) {
    if (phoneFormat[i] === '#') {
      formattedNumberArr[i] = phoneStr[hashCount] || phoneMask;
      hashCount++;
    }
  }
  return formattedNumberArr.join('');
}
